import React, { useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import reportService from '../../services/reportService';
import { AuthContext } from '../../context/AuthContext';

const DeveloperDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [showReminder, setShowReminder] = useState(true);
  const [todayReport, setTodayReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get current date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  // Check if user has submitted a report for today
  useEffect(() => {
    const checkTodayReport = async () => {
      try {
        // Get user reports for the current page
        const response = await reportService.getUserReports(1, 10);
        
        // Find if there's a report for today
        const todayReportExists = response.reports.find(report => 
          report.date.split('T')[0] === today
        );
        
        setTodayReport(todayReportExists);
      } catch (error) {
        console.error('Error checking today\'s report:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkTodayReport();
  }, [today]);
  
  return (
    <div className="page-fade-in">
      {showReminder && (
        <Alert variant="info" dismissible onClose={() => setShowReminder(false)}>
          <Alert.Heading>Daily Report Reminder</Alert.Heading>
          <p>
            Don't forget to submit your daily report at 11.59 PM today. Keeping your team updated helps everyone stay on the same page!
          </p>
        </Alert>
      )}
      
      <Row>
        <Col md={6} className="mb-4">
          <Card className="h-100">
            <Card.Header as="h5" className="bg-primary text-white">Quick Actions</Card.Header>
            <Card.Body>
              <div className="d-grid gap-3">
                <Button as={Link} to="/reports/new" variant="primary" size="lg">
                  Submit Today's Report
                </Button>
                <Button as={Link} to="/reports" variant="outline-primary" size="lg">
                  View My Reports
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} className="mb-4">
          <Card className="h-100">
            <Card.Header as="h5" className="bg-success text-white">Today's Status</Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" variant="success" />
                  <p className="mt-2">Checking report status...</p>
                </div>
              ) : (
                <>
                  <Card.Title>Date: {today}</Card.Title>
                  <Card.Text>
                    <strong>Status:</strong> {todayReport ? 'Report submitted' : 'Report not submitted yet'}
                  </Card.Text>
                  {todayReport ? (
                    <>
                      <Card.Text>
                        <strong>Submitted:</strong> {new Date(todayReport.created_at).toLocaleTimeString()}
                      </Card.Text>
                      <Button as={Link} to={`/reports/${todayReport.id}`} variant="info">
                        View Report
                      </Button>
                    </>
                  ) : (
                    <>
                      <Card.Text>
                        <strong>Reminder:</strong> Please include all tasks you've worked on today, hours spent, and any blockers you encountered.
                      </Card.Text>
                      <Button as={Link} to="/reports/new" variant="success">
                        Submit Now
                      </Button>
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row>
        <Col>
          <Card>
            <Card.Header as="h5" className="bg-info text-white">Reporting Tips</Card.Header>
            <Card.Body>
              <ul>
                <li>Be specific about tasks you completed</li>
                <li>Mention any challenges or blockers you faced</li>
                <li>Include your plan for tomorrow</li>
                <li>Submit your report at 11:59 PM daily</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DeveloperDashboard;
